import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash-es';
import React from 'react';
import { Form } from 'react-bootstrap';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { useAchievementsContext } from '../../../../../contexts/AchievementsContext/AchievementsContext';
import {
  AchievementCheckOperatorType,
  AchievementRewardType,
} from '../../../../../dorian-shared/types/achievement/Achievement';
import { IAchievementModal } from '../../AchievementsModalTypes';
import { getFirstUserVisibleReward, getFirstUserVisibleRewardIndex } from '../../utils';
import { useAchievementsModalContext } from '../../context/AchievementsModalContext';
import classes from '../AchievementField.module.scss';
import { customSelectStyles } from './customSelectStyles';
import { NoOptionsMessage } from './NoOptionMessage';
import { useAchievementWhen } from './useAchievementWhen';

export type AvailableWhenOptions = {
  label: string;
  value: string;
  type: 'memory' | 'episode' | 'manual';
}

export type AvailableWhenGroupedOptions = {
  label: string;
  options: AvailableWhenOptions[];
}

export type AchievementWhenFieldProps = {
  rowIndex: number;
  className?: string;
  disabled?: boolean;
  onChange?: (value: AvailableWhenOptions, actionMeta: ActionMeta<AvailableWhenOptions>) => void;
}

export function AchievementWhenField(props: AchievementWhenFieldProps) {
  const {
    rowIndex,
    className = 'position-relative',
    disabled = false,
    onChange,
  } = props;

  const { values, setValues } = useFormikContext<IAchievementModal[]>();
  const { isLoading: isContextLoading } = useAchievementsContext();
  const { isFormLoading } = useAchievementsModalContext();

  const {
    value, availableWhenGroupedOptions,
  } = useAchievementWhen(rowIndex);

  const handleWhenFieldChange = (selectedOption: SingleValue<AvailableWhenOptions>, actionMeta: ActionMeta<AvailableWhenOptions>) => {
    const newValues = [...values];
    const newValue = cloneDeep(newValues[rowIndex]);

    // Reset the check value when the type changes from manual to something else
    if (newValue.check.operator === AchievementCheckOperatorType.Manual && selectedOption?.type !== 'manual') {
      newValue.isManual = false;
      newValue.check = {
        variableId: 0,
        operator: '',
        value: '',
      };
    }

    if (selectedOption?.type === 'memory') {
      const newVariableId = Number.isNaN(selectedOption?.value)
        ? 0
        : Number(selectedOption.value);
      newValue.check.operator = AchievementCheckOperatorType.AtLeast;
      newValue.check.variableId = newVariableId;
    }

    if (selectedOption?.type === 'episode') {
      newValue.check.value = selectedOption?.value ? selectedOption?.value : '';
      newValue.check.operator = AchievementCheckOperatorType.HasCompleted;

      // Get the first user-visible reward (not HasAchievement)
      const firstUserVisibleReward = getFirstUserVisibleReward(newValue);
      const willSetRewardType = firstUserVisibleReward?.type || firstUserVisibleReward?.type === AchievementRewardType.None;
      if (willSetRewardType && firstUserVisibleReward) {
        firstUserVisibleReward.type = AchievementRewardType.Episode;
      }

      const willSetRewardDelaySec = firstUserVisibleReward?.delaySec === undefined || firstUserVisibleReward?.delaySec === null;
      if (willSetRewardDelaySec && firstUserVisibleReward) {
        firstUserVisibleReward.delaySec = 0;
      }
    }

    if (selectedOption?.type === 'manual') {
      newValue.isManual = true;
      newValue.check = {
        variableId: 0,
        operator: AchievementCheckOperatorType.Manual,
        value: '',
      };
    }

    newValues[rowIndex] = newValue;
    setValues([...newValues]);
    onChange?.(selectedOption as AvailableWhenOptions, actionMeta);
  };

  const errorMessage = value
    ? undefined
    : 'Required';

  const isLoading = isContextLoading || isFormLoading;

  return (
    <Form.Group className={className}>
      <Form.Label
        htmlFor="when"
        className={classes.label}
      >
        When
      </Form.Label>
      <Select<AvailableWhenOptions>
        isDisabled={isLoading || disabled}
        isLoading={isLoading}
        className="react-select-container"
        classNamePrefix="react-select"
        menuShouldBlockScroll
        tabSelectsValue
        placeholder="[Select]"
        options={availableWhenGroupedOptions}
        value={value}
        onChange={handleWhenFieldChange}
        menuPlacement="auto"
        components={{
          NoOptionsMessage,
        }}
        styles={customSelectStyles}
      />
      {errorMessage && !isLoading && (
        <div className="d-block invalid-tooltip">
          {errorMessage}
        </div>
      )}
    </Form.Group>
  );
}
